import { defineStore } from 'pinia'
import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { DateTime, type DateTime as DateTimeType } from 'luxon'
import type { AxiosError } from 'axios'
import type {
  LoginModel,
  LoginResponseModel,
  JWT,
  ForgotPasswordModel,
  SetPasswordModel,
  ResetPasswordModel,
  ChangePasswordModel,
  RegisterAdminModel,
  RegisterVeterinarianModel,
  RequestChangeEmailModel,
  ConfirmChangeEmailModel,
  CustomerLoginTokenRequestModel,
} from '@/models'
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model'
import { AuthenticationService } from '@/services/authentication.service'
import { useVeterinarianStore } from '@/stores/veterinarian'
import { useVeterinarianOfficeUserStore } from '@/stores/veterinarian-office-user'

/**
 * Authentication Store (Day 3 Implementation)
 *
 * Full Pinia store with JWT authentication, role-based access control,
 * password management, and public tag flow state management.
 */
export const useAuthStore = defineStore('auth', () => {
  // Services
  const authService = new AuthenticationService()

  // State - UI (4 properties)
  const isLoginComponent: Ref<boolean> = ref(true)
  const showLoading: Ref<boolean> = ref(false)
  const showButtonLoading: Ref<boolean> = ref(false)
  const prefillEmail: Ref<string> = ref('')

  // State - Public Tag Flow (3 properties)
  const acceptedTermsToViewCustomerData: Ref<boolean> = ref(false)
  const tagHasMatch: Ref<boolean> = ref(true)
  const ownerAuthentication: Ref<boolean> = ref(false)

  // State - Auth (2 properties)
  const loginResponse: Ref<LoginResponseModel | null> = ref(null)
  const errorResponse: Ref<BaseAPIErrorModel | null> = ref(null)

  // State - Success Flags (10 properties)
  const forgotPasswordSuccess: Ref<boolean | null> = ref(null)
  const setPasswordSuccess: Ref<boolean | null> = ref(null)
  const resetPasswordSuccess: Ref<boolean | null> = ref(null)
  const changePasswordSuccess: Ref<boolean | null> = ref(null)
  const registerAdminSuccess: Ref<boolean | null> = ref(null)
  const registerVeterinarianSuccess: Ref<boolean | null> = ref(null)
  const requestChangeEmailSuccess: Ref<boolean | null> = ref(null)
  const confirmChangeEmailSuccess: Ref<boolean | null> = ref(null)
  const confirmChangeEmailIsCustomer: Ref<boolean | null> = ref(null)
  const customerLoginTokenRequestSuccess: Ref<boolean | null> = ref(null)

  // Getters - Error Handling
  const errorMessages: ComputedRef<string[]> = computed(() => {
    return errorResponse.value?.getAllErrorMessages() ?? []
  })

  const hasErrorMessages: ComputedRef<boolean> = computed(() => {
    return errorMessages.value.length > 0
  })

  // Getters - JWT Parsing
  const jwtData: ComputedRef<JWT | null> = computed(() => {
    if (!loginResponse.value?.token) {
      return null
    }

    try {
      const parts = loginResponse.value.token.split('.')
      if (parts.length < 2 || !parts[1]) {
        console.error('Invalid JWT token format')
        return null
      }
      return JSON.parse(atob(parts[1]))
    } catch (error) {
      console.error('Failed to parse JWT token:', error)
      return null
    }
  })

  const jwtEmail: ComputedRef<string> = computed(() => {
    return jwtData.value?.email ?? ''
  })

  const jwtRoles: ComputedRef<string[]> = computed(() => {
    const roles = jwtData.value?.role ?? []
    // CRITICAL: API can return role as either string or array!
    return typeof roles === 'string' ? [roles] : roles
  })

  const jwtExpires: ComputedRef<DateTimeType | null> = computed(() => {
    const exp = jwtData.value?.exp
    if (!exp) {
      return null
    }

    const expires = DateTime.fromSeconds(exp)
    if (!expires.isValid) {
      return null
    }

    return expires
  })

  const jwtVeterinarianOfficeId: ComputedRef<string> = computed(() => {
    return jwtData.value?.veterinarianOfficeId ?? ''
  })

  // Getters - Convenience
  const token: ComputedRef<string> = computed(() => {
    return loginResponse.value?.token ?? ''
  })

  const isAuthenticated: ComputedRef<boolean> = computed(() => {
    if (!token.value) {
      return false
    }

    // Check if token is expired
    if (jwtExpires.value) {
      return jwtExpires.value > DateTime.now()
    }

    return false
  })

  const isAdmin: ComputedRef<boolean> = computed(() => {
    return jwtRoles.value.includes('Admin')
  })

  const isVet: ComputedRef<boolean> = computed(() => {
    return jwtRoles.value.includes('Veterinarian')
  })

  const isVetUser: ComputedRef<boolean> = computed(() => {
    return jwtRoles.value.includes('VeterinarianUser')
  })

  // Getters - Public Tag Flow
  const hasAcceptedTerms: ComputedRef<boolean> = computed(() => {
    return acceptedTermsToViewCustomerData.value
  })

  const hasTagGottaMatch: ComputedRef<boolean> = computed(() => {
    return tagHasMatch.value
  })

  const hasOwnerAuthenticated: ComputedRef<boolean> = computed(() => {
    return ownerAuthentication.value
  })

  // Actions - UI State
  function changeComponentState(isLogin: boolean, email?: string): void {
    isLoginComponent.value = isLogin
    prefillEmail.value = email ?? ''
  }

  // Actions - Core Auth
  async function login(credentials: LoginModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      let response: LoginResponseModel

      // Handle customer vs admin/vet login
      if (credentials.isCustomer) {
        response = await authService.customerLogin(credentials)
      } else {
        response = await authService.login(credentials)
      }

      loginResponse.value = response

      // Persist token for session restoration
      localStorage.setItem('auth_token', response.token)
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  // Single-flight guard for refreshToken(): backend refresh tokens are
  // single-use + rotated, and every API service owns its own axios 401
  // interceptor, so N calls expiring at once would fire N parallel
  // POST /RefreshToken with the SAME cookie. The first rotates/revokes the
  // token; the rest carry the now-revoked token → 404 "User not found".
  // Sharing one in-flight promise collapses them into a single refresh.
  let refreshInFlight: Promise<void> | null = null

  async function refreshToken(): Promise<void> {
    // Concurrent callers join the in-flight refresh instead of starting another.
    if (refreshInFlight) {
      return refreshInFlight
    }

    refreshInFlight = (async () => {
      try {
        const response = await authService.refreshToken()
        loginResponse.value = response

        // Update localStorage
        localStorage.setItem('auth_token', response.token)
      } catch (error: unknown) {
        const axiosError = error as AxiosError
        if (axiosError?.response?.data) {
          console.log(axiosError.response.data)
        } else {
          // Message only. The AxiosError itself carries config.headers.Authorization,
          // so logging the object puts the bearer token in devtools and in anything
          // capturing the console.
          console.log(axiosError?.message ?? error)
        }

        // Re-throw so callers decide whether to clear the session. The
        // existing token is intentionally NOT cleared here: a transient refresh
        // failure (e.g. missing/blocked httpOnly refresh cookie in a Playwright
        // context) must not wipe a still-valid JWT. `restoreSession` catches
        // this and clears; `refreshTokenIfNeeded` swallows and leaves the live
        // session intact until the token naturally expires or a 401 surfaces.
        throw error
      } finally {
        // Clear the latch once settled so the next expiry can refresh again.
        refreshInFlight = null
      }
    })()

    return refreshInFlight
  }

  async function refreshTokenIfNeeded(): Promise<void> {
    // Check if token exists and is close to expiring (within 5 minutes)
    if (!token.value || !jwtExpires.value) {
      return
    }

    const fiveMinutesFromNow = DateTime.now().plus({ minutes: 5 })
    if (jwtExpires.value < fiveMinutesFromNow) {
      try {
        await refreshToken()
      } catch {
        // Best-effort refresh: keep the existing session. A later 401 from
        // the API will be handled by the axios interceptor / normal logout
        // flow when the token truly expires.
      }
    }
  }

  async function revokeToken(): Promise<void> {
    try {
      errorResponse.value = null
      await authService.revokeToken()
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    }
  }

  async function logout(): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null

      // First revoke the refresh token
      await revokeToken()

      // Then logout
      await authService.logout()
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
    } finally {
      // Reset dependent stores (always, even on error)
      const veterinarianStore = useVeterinarianStore()
      const vetOfficeUserStore = useVeterinarianOfficeUserStore()
      veterinarianStore.resetState()
      vetOfficeUserStore.resetState()

      // TODO: Reset other stores when they exist
      // const adminUserStore = useAdminUserStore()
      // const customersStore = useCustomersStore()
      // const registeredTagStore = useRegisteredTagStore()
      // adminUserStore.resetState()
      // customersStore.resetState()
      // registeredTagStore.resetState()

      // Reset this store's auth state
      loginResponse.value = null
      acceptedTermsToViewCustomerData.value = false
      tagHasMatch.value = true
      ownerAuthentication.value = false

      // Clear localStorage
      localStorage.removeItem('auth_token')

      showLoading.value = false
    }
  }

  async function restoreSession(): Promise<void> {
    const storedToken = localStorage.getItem('auth_token')
    if (!storedToken) {
      return
    }

    // Set token
    loginResponse.value = { token: storedToken }

    // Validate by refreshing. Only clear the stored token when the JWT is
    // truly expired — a transient refresh failure (e.g. missing httpOnly
    // refresh cookie) must not wipe a still-valid session across a full
    // page reload / direct URL navigation.
    try {
      await refreshToken()
    } catch {
      if (!isAuthenticated.value) {
        loginResponse.value = null
        localStorage.removeItem('auth_token')
      }
    }
  }

  // Actions - Password Management
  async function forgotPassword(model: ForgotPasswordModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.forgotPassword(model)

      // Success
      forgotPasswordSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function setPassword(model: SetPasswordModel): Promise<void> {
    try {
      errorResponse.value = null

      await authService.setPassword(model)

      // Success
      setPasswordSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  async function resetPassword(model: ResetPasswordModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.resetPassword(model)

      // Success
      resetPasswordSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function changePassword(model: ChangePasswordModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.changePassword(model)

      // Success
      changePasswordSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  // Actions - Registration
  async function registerAdmin(model: RegisterAdminModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.registerAdmin(model)

      // Success
      registerAdminSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function registerVeterinarian(model: RegisterVeterinarianModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.registerVeterinarian(model)

      // Success
      registerVeterinarianSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  // Actions - Email Management
  async function requestChangeEmail(model: RequestChangeEmailModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.requestChangeEmail(model)

      // Success
      requestChangeEmailSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function confirmChangeEmail(model: ConfirmChangeEmailModel): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null

      const result = await authService.confirmChangeEmail(model)

      // Success
      confirmChangeEmailSuccess.value = true
      confirmChangeEmailIsCustomer.value = result.isCustomer
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  // Actions - Customer Login Token Request
  async function customerLoginTokenRequest(model: CustomerLoginTokenRequestModel): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      await authService.customerLoginTokenRequest(model)

      // Success
      customerLoginTokenRequestSuccess.value = true
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  // Actions - Public Tag Flow
  async function acceptTerms(accepted: boolean): Promise<void> {
    showLoading.value = true

    await authService.setAcceptedTerms()

    acceptedTermsToViewCustomerData.value = accepted
    showLoading.value = false
  }

  function tagMatch(hasMatch: boolean): void {
    tagHasMatch.value = hasMatch
  }

  function tagResetMatch(): void {
    tagHasMatch.value = true
  }

  function authenticateOwner(authenticated: boolean): void {
    ownerAuthentication.value = authenticated
  }

  function resetOwnerAuthentication(): void {
    ownerAuthentication.value = false
  }

  // Return public API
  return {
    // State - UI
    isLoginComponent,
    showLoading,
    showButtonLoading,
    prefillEmail,

    // State - Public Tag Flow
    acceptedTermsToViewCustomerData,
    tagHasMatch,
    ownerAuthentication,

    // State - Auth
    loginResponse,
    errorResponse,

    // State - Success Flags
    forgotPasswordSuccess,
    setPasswordSuccess,
    resetPasswordSuccess,
    changePasswordSuccess,
    registerAdminSuccess,
    registerVeterinarianSuccess,
    requestChangeEmailSuccess,
    confirmChangeEmailSuccess,
    confirmChangeEmailIsCustomer,
    customerLoginTokenRequestSuccess,

    // Getters - Error Handling
    errorMessages,
    hasErrorMessages,

    // Getters - JWT Parsing
    jwtData,
    jwtEmail,
    jwtRoles,
    jwtExpires,
    jwtVeterinarianOfficeId,

    // Getters - Convenience
    token,
    isAuthenticated,
    isAdmin,
    isVet,
    isVetUser,

    // Getters - Public Tag Flow
    hasAcceptedTerms,
    hasTagGottaMatch,
    hasOwnerAuthenticated,

    // Actions - UI State
    changeComponentState,

    // Actions - Core Auth
    login,
    refreshToken,
    refreshTokenIfNeeded,
    revokeToken,
    logout,
    restoreSession,

    // Actions - Password Management
    forgotPassword,
    setPassword,
    resetPassword,
    changePassword,

    // Actions - Registration
    registerAdmin,
    registerVeterinarian,

    // Actions - Email Management
    requestChangeEmail,
    confirmChangeEmail,

    // Actions - Customer Login Token Request
    customerLoginTokenRequest,

    // Actions - Public Tag Flow
    acceptTerms,
    tagMatch,
    tagResetMatch,
    authenticateOwner,
    resetOwnerAuthentication,
  }
})
